import { NgModule, ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { APP_BASE_HREF, DatePipe } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { MockModules } from 'src/test/mocks/mock-modules'; // for Mock startup mode (different than `npm run test`)

import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { L10nLoader, L10nTranslationModule, L10nTranslationService, L10nTranslationLoader } from 'angular-l10n';
import { l10nConfig, l10nInit, l10nToken, l10nPreload } from './l10n-config';

import { AppEnv } from './app.config';

import { RoutingModule } from './app-routing.module';
import { SharedModule } from './shared/shared.module';
import { ApiConfig } from './shared/api-config/api-config';
import { BoxwoodApiModule } from './boxwood-api';
import { DirectoryApiModule } from './directory-api';
import { PlatformDirectoryApiModule } from './platform-directory-api';
import { HandweaveApiModule } from './handweave-api';
import { HickoryApiModule } from './hickory-api';
import { WalnutApiModule } from './walnut/walnut-api';
import { UsageApiModule } from './usage-api';

import { AppComponent } from './app.component';
import { HomeComponent } from './home.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';

// Services
import { AuthService, AUTH_USE_MOCK } from './shared/authentication/auth.service';
import { FeatureToggleService } from './shared/feature-toggle.service';
import { NavService } from './shared/navigation/nav.service';
import { IncidentService } from './shared/incident/incident.service';
import { FeedbackService } from './shared/feedback/feedback.service';
import { FormSuccessService } from './shared/form-success.service';
import { ApiSelectorService } from './shared/api-selector/api-selector.service';

// Feature Modules
import { UserModule } from './user/user.module';
import { MessagingModule } from './shared/messaging/messaging.module';
import { GlobalErrorHandler } from './error-handler/error-handler';
import { HttpInterceptorService } from './shared/http-interceptor/http-interceptor.service';

import {
  LazyMapsApiLoader,
  GOOGLE_MAPS_API_CONFIG,
  MapsApiLoader,
  MAPS_USE_MOCK,
  GoogleMapsConfig,
} from './shared/google-maps';
import { LayoutService } from './shared/layout/layout.service';
import { CombinedDirectoryService } from './shared/directory/combined/combined-directory.service';
import { LinksService } from './shared/links/links.service';
import { BusService } from './shared/bus.service';
import { NgxIndexedDBModule } from 'ngx-indexed-db';
import { AppDatabaseService, appDbConfig } from './shared/app-database/app-database.service';
import { environment } from 'src/environments/environment';
import { GoogleMapsModule } from '@angular/google-maps';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { SafeDatePipe } from './shared/pipes/fix-date/fix-date.pipe';

@NgModule({
  imports: [
    // System modules
    BrowserModule, // Use BrowserModule instead of CommonModule for the root module
    BrowserAnimationsModule,
    L10nTranslationModule.forRoot(l10nConfig, l10nToken),
    NgxIndexedDBModule.forRoot(appDbConfig),
    FormsModule,

    // API modules
    BoxwoodApiModule.forRoot(ApiConfig.boxwoodConfigFactory),
    DirectoryApiModule.forRoot(ApiConfig.directoryConfigFactory),
    PlatformDirectoryApiModule.forRoot(ApiConfig.platformDirectoryConfigFactory),
    UsageApiModule.forRoot(ApiConfig.usageConfigFactory),
    HandweaveApiModule.forRoot(ApiConfig.handweaveConfigFactory),
    HickoryApiModule.forRoot(ApiConfig.hickoryConfigFactory),
    WalnutApiModule.forRoot(ApiConfig.walnutConfigFactory),

    // Standalone
    SafeDatePipe,

    // Shared modules
    SharedModule,
    UserModule,
    GoogleMapsModule,
    MessagingModule,
    RoutingModule,   // Must be (almost) last

    // Mock modules: for `npm run start:mock` -- 
    // Note: this is not used for unit tests (`npm run test`)
    ...MockModules.mockModules
  ],
  declarations: [
    AppComponent,
    HomeComponent,
    PageNotFoundComponent,
  ],
  providers: [
    provideAppInitializer(() => {
      const initializerFn = (l10nPreload)(inject(L10nTranslationService), inject(L10nTranslationLoader));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = (l10nInit)(inject(L10nLoader));
      return initializerFn();
    }),
    { provide: HTTP_INTERCEPTORS, useClass: HttpInterceptorService, multi: true },
    { provide: GOOGLE_MAPS_API_CONFIG, useClass: GoogleMapsConfig },
    { provide: MapsApiLoader, useClass: LazyMapsApiLoader },
    { provide: APP_BASE_HREF, useValue: '/' },
    { provide: DatePipe, useClass: SafeDatePipe },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: AUTH_USE_MOCK, useValue: environment.env === AppEnv.mock },
    { provide: MAPS_USE_MOCK, useValue: environment.env === AppEnv.mock },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useFactory: (overlay: Overlay) => ({
        scrollStrategy: overlay.scrollStrategies.noop()
      }),
      deps: [Overlay]
    },
    SafeDatePipe,
    AuthService,
    FeatureToggleService,
    NavService,
    IncidentService,
    FeedbackService,
    FormSuccessService,
    LayoutService,
    CombinedDirectoryService,
    LinksService,
    BusService,
    ApiSelectorService,
    AppDatabaseService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
